import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import { useState } from 'react';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';

import InputWithHoverEditButtonField from 'dashboard/components/FormControls/InputWithHoverEditButtonField';
import { OptionType } from 'dashboard/components/ReactSelect';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import {
  MatchRequirementKeys,
  matchRequirementMatchLevelName,
  MatchRequirementProfile,
} from 'dashboard/models/match-requirement-profile';
import { MatchGroup, MatchRequirement } from 'dashboard/models/verification-check';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Select, { Option } from 'design-system/components/Select';
import Sticker from 'design-system/components/Sticker/Sticker';
import { Paragraph } from 'design-system/components/Text';
import { COUNTRIES, CountryCode } from 'lib/constants/locales';

const GroupContainer = styled.div`
  padding: ${Spacing[5]};
  border-radius: ${BORDER_RADIUSES.rounded};
  background-color: ${({ theme }) => theme.color.background.secondary};
  margin-bottom: ${Spacing[2]};
`;

const GroupDescriptionContainer = styled.div`
  display: flex;
  margin-bottom: ${Spacing[2]};
`;

const MatchRequirementContainer = styled.div<{ editable: boolean }>`
  display: grid;
  grid-template-columns: 50px 1fr;
  align-items: center;
  grid-gap: ${Spacing[2]};
  padding: ${Spacing[1]} 0;
  overflow-x: hidden;
  ${({ editable }) => {
    if (editable)
      return css`
        margin-bottom: ${Spacing[1]};
      `;
  }}
`;

const MatchRequirementDescription = styled.button<{ editable: boolean; selected: boolean }>`
  display: grid;
  grid-template-columns: min-content 60px min-content;
  align-items: center;
  grid-gap: ${Spacing[2]};
  background-color: ${({ theme }) => theme.color.background.primary};
  padding: ${Spacing[1]} ${Spacing[2]};
  color: ${({ editable, theme }) =>
    editable ? theme.color.text.primary : theme.color.text.disabled};
  border-radius: ${BORDER_RADIUSES.rounded};
  border-color: ${({ theme }) => theme.color.background.primary};
  border-width: 2px;
  border-style: solid;

  ${({ editable }) => {
    if (editable) {
      return css`
        &:hover {
          border-color: ${({ theme }) => theme.color.border.primary};
        }
      `;
    }
  }}

  ${({ editable, selected }) => {
    if (editable && selected) {
      return css`
        border-color: ${({ theme }) => theme.color.border.active} !important;
      `;
    }
  }}
`;

const TextContainer = styled.div<{ editable: boolean }>`
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  color: ${({ editable, theme }) =>
    editable ? theme.color.text.primary : theme.color.text.disabled};
`;

const MatchTextContainer = styled.div`
  font-size: ${({ theme }) => theme.text.paragraph.sm.fontSize};
  text-align: center;
`;

const CountrySelect = styled(Select)`
  width: 70px;
`;

const CountryMultiSelect = styled(MultiSelect)`
  width: auto;
  margin-left: ${Spacing[4]};
  margin-right: auto;
  height: '20px';
`;

const COUNTRY_LIST = {
  default: 'All countries',
  ...COUNTRIES,
};

const COUNTRY_OPTIONS: OptionType<CountryCode>[] = Object.keys(COUNTRY_LIST).map(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (option: CountryCode) => ({
    value: option,
    label: `${COUNTRY_LIST[option]} (${option})`,
  }),
);

interface MatchRequirementsViewProps {
  matchGroups: MatchGroup[];
  usesMatchGroups: boolean;
  matchRequirementProfiles: MatchRequirementProfile[];
  selectedMatchRequirement: MatchRequirementKeys | null;
  editable: boolean;
  showCountryCode: boolean;
  handleAddMatchRequirementToMatchGroup?: (matchGroupIndex: number) => void;
  handleDeleteMatchGroup?: (matchGroupIndex: number) => void;
  handleAddMatchGroup?: (countryCode: CountryCode) => void;
  handleUpdateMatchGroupName?: (matchGroupIndex: number, matchGroupName: string) => void;
  handleUpdateMatchGroupCountries?: (matchGroupIndex: number, countries: CountryCode[]) => void;
  handleMatchRequirementSelected?: (matchGroupIndex: number, matchRequirementIndex: number) => void;
}

const MatchRequirementsView = ({
  matchGroups,
  usesMatchGroups,
  matchRequirementProfiles,
  selectedMatchRequirement,
  editable,
  showCountryCode = false,
  handleAddMatchRequirementToMatchGroup,
  handleAddMatchGroup,
  handleDeleteMatchGroup,
  handleUpdateMatchGroupName,
  handleUpdateMatchGroupCountries,
  handleMatchRequirementSelected,
}: MatchRequirementsViewProps) => {
  const [countryCode, setCountryCode] = useState(null);

  return (
    <Card data-test='match-requirements-view'>
      <Card.Body>
        <Card.Title
          actions={
            showCountryCode && (
              <CountrySelect
                size='md'
                placeholder='--'
                value={countryCode}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                onValueChange={setCountryCode}
              >
                {/* @ts-expect-error [strict-migration] The following line is not strict; please fix! */}
                <Option value={null}>--</Option>
                {Object.keys(COUNTRIES).map((countryCode) => (
                  <Option key={countryCode} value={countryCode}>
                    {countryCode}
                  </Option>
                ))}
              </CountrySelect>
            )
          }
        >
          <Paragraph size='md' weight='bold' color={editable ? 'primary' : 'disabled'}>
            Match Requirements
          </Paragraph>
        </Card.Title>
        {matchGroups &&
          matchGroups.map(
            (matchGroup: MatchGroup, index: number) =>
              (!countryCode ||
                //I f the match group has a single country code, check if it matches the selected country code
                ('country-code' in matchGroup && matchGroup['country-code'] === countryCode) ||
                // If the match group has multiple country codes, check if the selected country code is included
                ('country-codes' in matchGroup &&
                  (matchGroup['country-codes'] as CountryCode[]).includes(countryCode))) && (
                <MatchGroupRow
                  key={`match-requirements-view__match-group-${index}`}
                  matchGroup={matchGroup}
                  matchRequirementProfiles={matchRequirementProfiles}
                  matchGroupIndex={index}
                  matchGroupLength={matchGroups.length}
                  showCountryCode={showCountryCode}
                  usesMatchGroups={usesMatchGroups}
                  editable={editable}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  selectedMatchRequirement={selectedMatchRequirement}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleAddMatchRequirementToMatchGroup={handleAddMatchRequirementToMatchGroup}
                  handleUpdateMatchGroupName={handleUpdateMatchGroupName}
                  handleUpdateMatchGroupCountries={handleUpdateMatchGroupCountries}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  handleMatchRequirementSelected={handleMatchRequirementSelected}
                  handleDeleteMatchGroup={handleDeleteMatchGroup}
                />
              ),
          )}
        {editable && usesMatchGroups && (
          <Button
            id='match-requirements-view__new-match-group-button'
            data-test='match-requirements-view__new-match-group-button'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              handleAddMatchGroup(countryCode);
            }}
            variant='basic'
            width='button'
          >
            Add match group
          </Button>
        )}
      </Card.Body>
    </Card>
  );
};

interface MatchGroupRowProps {
  matchGroup: MatchGroup;
  matchRequirementProfiles: MatchRequirementProfile[];
  matchGroupIndex: number;
  matchGroupLength: number;
  selectedMatchRequirement: MatchRequirementKeys;
  showCountryCode: boolean;
  usesMatchGroups: boolean;
  editable: boolean;
  handleAddMatchRequirementToMatchGroup: (matchGroupIndex: number) => void;
  handleUpdateMatchGroupName?: (matchGroupIndex: number, matchGroupName: string) => void;
  handleUpdateMatchGroupCountries?: (matchGroupIndex: number, countries: CountryCode[]) => void;
  handleDeleteMatchGroup?: (matchGroupIndex: number) => void;
  handleMatchRequirementSelected: (matchGroupIndex: number, matchRequirementIndex: number) => void;
}

/**
 * Returns a MatchGroupRow, displaying a match group in a card format
 *
 * @param matchGroup - The match group to be rendered
 * @param matchRequirementProfiles - All possible match requirement profiles for the object
 * @param matchGroupIndex - The index of the match group to be rendered
 * @param selectedMatchRequirement - An object describing which match requirement is currently selected
 * @param editable - Determines whether this match requirement is editable
 * @param handleMatchRequirementSelected - A function to handle when a match requirement is selected
 *
 * @returns {MatchRequirementRow}
 */
const MatchGroupRow = ({
  matchGroup,
  matchRequirementProfiles,
  matchGroupIndex,
  matchGroupLength,
  selectedMatchRequirement,
  showCountryCode,
  usesMatchGroups,
  editable,
  handleAddMatchRequirementToMatchGroup,
  handleUpdateMatchGroupName,
  handleUpdateMatchGroupCountries,
  handleMatchRequirementSelected,
  handleDeleteMatchGroup,
}: MatchGroupRowProps) => {
  const renderDeleteButton = usesMatchGroups && handleDeleteMatchGroup && editable;

  const countryTextMapper = useLabelTextMapper(COUNTRY_OPTIONS, 'countries', {
    emptyIsAll: false,
  });

  return (
    <GroupContainer
      id={`match-requirements-view__match-group-${matchGroupIndex}`}
      data-test={`match-requirements-view__match-group-${matchGroupIndex}`}
    >
      {usesMatchGroups && (
        <GroupDescriptionContainer>
          <InputWithHoverEditButtonField
            fieldName='match-group-description'
            displayElement={<Paragraph size='md' style={{ height: '20px' }} />}
            displayPlaceholder={matchGroup.description}
            placeholder={matchGroup.description}
            disabled={!editable}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            onBlur={(value: string) => handleUpdateMatchGroupName(matchGroupIndex, value)}
          />
          {showCountryCode && 'country-code' in matchGroup && (
            <Sticker
              variant={editable ? 'purple' : 'primary'}
              style={{ marginRight: 'auto', marginLeft: Spacing[2], height: '20px' }}
            >
              {matchGroup['country-code']}
            </Sticker>
          )}
          {showCountryCode && 'country-codes' in matchGroup && (
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            <CountryMultiSelect
              size='sm'
              value={matchGroup['country-codes'] as CountryCode[]}
              onChange={(values: CountryCode[]) =>
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                handleUpdateMatchGroupCountries(matchGroupIndex, values)
              }
              labelTextMapper={countryTextMapper}
              selectOptionValues={COUNTRY_OPTIONS}
              disabled={!editable}
              searchEnabled={true}
            />
          )}
          {renderDeleteButton && (
            <Button
              id={`match-requirements-view__match-group-${matchGroupIndex}-delete-button`}
              onClick={() => handleDeleteMatchGroup(matchGroupIndex)}
              variant='basic'
              size='sm'
              width='auto'
              disabled={matchGroupLength === 1 && matchGroupIndex === 0}
              style={{ marginLeft: 'auto' }}
            >
              <TrashIcon />
            </Button>
          )}
        </GroupDescriptionContainer>
      )}
      {matchGroup['match-requirements'].map((matchRequirement, matchRequirementIndex) => (
        <MatchRequirementRow
          key={`match-requirements-view__match-requirement-${matchGroupIndex}-${matchRequirementIndex}`}
          matchRequirement={matchRequirement}
          matchRequirementProfiles={matchRequirementProfiles}
          matchGroupIndex={matchGroupIndex}
          matchRequirementIndex={matchRequirementIndex}
          editable={editable}
          selectedMatchRequirement={selectedMatchRequirement}
          handleMatchRequirementSelected={handleMatchRequirementSelected}
        />
      ))}
      {editable && (
        <Button
          id={`match-requirements-view__match-group-${matchGroupIndex}-add-match-requirement-button`}
          data-test={`match-requirements-view__match-group-${matchGroupIndex}-add-match-requirement-button`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleAddMatchRequirementToMatchGroup(matchGroupIndex);
          }}
          variant='basic'
          width='auto'
        >
          {matchGroup['match-requirements'].length > 0 ? 'And' : 'Add match requirement'}
        </Button>
      )}
    </GroupContainer>
  );
};

interface MatchRequirementRowProps {
  matchRequirement: MatchRequirement;
  matchRequirementProfiles: MatchRequirementProfile[];
  matchGroupIndex: number;
  matchRequirementIndex: number;
  selectedMatchRequirement: MatchRequirementKeys;
  editable: boolean;
  handleMatchRequirementSelected: (matchGroupIndex: number, matchRequirementIndex: number) => void;
}

/**
 * Returns a MatchRequirementRow
 *
 * @param matchRequirement - The match requirement to be rendered
 * @param matchRequirementProfiles - All possible match requirement profiles that this row could be set to
 * @param matchGroupIndex - The index of the match group to be rendered
 * @param matchRequirementIndex - The index of the match requirement to be rendered
 * @param selectedMatchRequirement - An object describing which match requirement is currently selected
 * @param editable - Determines whether this match requirement is editable
 * @param handleMatchRequirementSelected - A function to handle when a match requirement is selected
 *
 * @returns {MatchRequirementRow}
 */
const MatchRequirementRow = ({
  matchRequirement,
  matchRequirementProfiles,
  matchGroupIndex,
  matchRequirementIndex,
  selectedMatchRequirement,
  editable,
  handleMatchRequirementSelected,
}: MatchRequirementRowProps) => {
  const attributeName = matchRequirement.description;
  const matchLevelName = matchRequirementMatchLevelName(matchRequirement, matchRequirementProfiles);
  const selected =
    selectedMatchRequirement != null &&
    selectedMatchRequirement['match-group-index'] === matchGroupIndex &&
    selectedMatchRequirement['match-requirement-index'] === matchRequirementIndex;
  return (
    <MatchRequirementContainer
      id={`match-requirements-view__match-requirement-${matchGroupIndex}-${matchRequirementIndex}`}
      data-test={`match-requirements-view__match-requirement-${matchGroupIndex}-${matchRequirementIndex}`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        editable && handleMatchRequirementSelected(matchGroupIndex, matchRequirementIndex);
      }}
      editable={editable}
    >
      <TextContainer editable={editable}>
        {matchRequirementIndex === 0 ? (matchGroupIndex === 0 ? 'If' : 'Or if') : 'And'}
      </TextContainer>
      <MatchRequirementDescription editable={editable} selected={selected}>
        {attributeName == null ? (
          <Sticker> Empty match requirement </Sticker>
        ) : (
          <>
            <Sticker disabled={!editable}> {attributeName}</Sticker>
            <MatchTextContainer>match is</MatchTextContainer>
            <Sticker disabled={!editable}> {matchLevelName}</Sticker>
          </>
        )}
      </MatchRequirementDescription>
    </MatchRequirementContainer>
  );
};

export default MatchRequirementsView;
